import { API_BASE_URL, API_DEFAULT_TIMEOUT_MS, CLIENT_CAPABILITIES, HEADERS } from '@/constants/api';
import {
  ApiError,
  type ApiErrorBody,
  NetworkError,
  RateLimitError,
  ServerError,
  TimeoutError,
  UnauthenticatedError,
  ValidationError,
} from '@/services/errors';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  signal?: AbortSignal;
  idempotencyKey?: string;
  headers?: Record<string, string>;
  timeoutMs?: number;
  /** Skip auth header for this call (e.g. login). */
  skipAuth?: boolean;
  /**
   * Don't treat a 401 on this call as "the session is dead."
   *
   * A 401 normally fires `unauthenticated`, which clears the session and
   * bounces to login. That's right for a request the user initiated — they
   * asked for something and we can't serve it. It's wrong for a background
   * write nobody asked for: a stale token surfacing on an analytics ping
   * would sign someone out mid-task, and no `.catch()` can prevent it because
   * the listener fires synchronously before the promise rejects.
   *
   * The call still rejects with UnauthenticatedError; it just doesn't take
   * the session down with it.
   */
  suppressUnauthenticated?: boolean;
}

interface InternalRequestArgs {
  method: HttpMethod;
  path: string;
  body?: unknown;
  options?: RequestOptions;
}

type Listener = () => void;

/**
 * ApiClient
 *
 * The single entrypoint for every HTTP call this app makes. Components and
 * screens never call `fetch` directly — they go through service modules
 * (`services/auth.ts`, `services/deals.ts`, ...) which use this client.
 *
 * Responsibilities:
 *  - Resolve the base URL + path.
 *  - Inject `Authorization: Bearer <token>` from the token getter (set by
 *    auth store on hydrate).
 *  - JSON-encode bodies, set Content-Type.
 *  - Apply per-request timeouts via AbortController.
 *  - Normalize every non-2xx response into a typed ApiError subclass.
 *  - Emit `unauthenticated` on 401 so the auth store can clear and the
 *    router can bounce to login.
 *  - Surface 429 with parsed Retry-After.
 *  - Pass through `Idempotency-Key` for cashout retry safety.
 *
 * Usage from a service:
 *   import { apiClient } from '@/services/api';
 *   const user = await apiClient.get<User>('/api/v1/auth/me');
 */
export class ApiClient {
  private baseUrl: string;
  private getToken: () => string | null = () => null;
  private unauthenticatedListeners: Set<Listener> = new Set();

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /** Auth store calls this on init/hydrate to wire token resolution. */
  setTokenGetter(getter: () => string | null): void {
    this.getToken = getter;
  }

  /** Auth store subscribes here so it can clear local session on a 401. */
  onUnauthenticated(listener: Listener): () => void {
    this.unauthenticatedListeners.add(listener);
    return () => {
      this.unauthenticatedListeners.delete(listener);
    };
  }

  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'GET', path, options });
  }

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'POST', path, body, options });
  }

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'PATCH', path, body, options });
  }

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'PUT', path, body, options });
  }

  del<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>({ method: 'DELETE', path, body, options });
  }

  private async request<T>({ method, path, body, options }: InternalRequestArgs): Promise<T> {
    const url = this.buildUrl(path);
    const headers = this.buildHeaders(body, options);
    const { signal, cleanup } = this.buildSignal(options);

    if (__DEV__) {
      console.log(`[api] → ${method} ${path}`);
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (err) {
      cleanup();
      throw this.toFetchError(err);
    }
    cleanup();

    if (__DEV__) {
      console.log(`[api] ← ${response.status} ${method} ${path}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const parsed = await this.parseBody(response);

    if (response.ok) {
      return parsed as T;
    }

    throw this.toApiError(response, parsed, options);
  }

  private buildUrl(path: string): string {
    if (path.startsWith('http')) return path;
    const base = this.baseUrl.replace(/\/+$/, '');
    const suffix = path.startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  }

  private buildHeaders(body: unknown, options?: RequestOptions): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      [HEADERS.capabilities]: CLIENT_CAPABILITIES.join(','),
      ...(options?.headers ?? {}),
    };

    if (body !== undefined) {
      headers[HEADERS.contentType] = 'application/json';
    }

    if (!options?.skipAuth) {
      const token = this.getToken();
      if (token) {
        headers[HEADERS.authorization] = `Bearer ${token}`;
      }
    }

    if (options?.idempotencyKey) {
      headers[HEADERS.idempotencyKey] = options.idempotencyKey;
    }

    return headers;
  }

  private buildSignal(options?: RequestOptions): { signal: AbortSignal; cleanup: () => void } {
    const timeoutMs = options?.timeoutMs ?? API_DEFAULT_TIMEOUT_MS;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort('timeout'), timeoutMs);

    const callerSignal = options?.signal;
    let abortHandler: (() => void) | undefined;

    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort(callerSignal.reason);
      } else {
        abortHandler = () => controller.abort(callerSignal.reason);
        callerSignal.addEventListener('abort', abortHandler, { once: true });
      }
    }

    const cleanup = () => {
      clearTimeout(timeoutId);
      if (callerSignal && abortHandler) {
        callerSignal.removeEventListener('abort', abortHandler);
      }
    };

    return { signal: controller.signal, cleanup };
  }

  private async parseBody(response: Response): Promise<unknown> {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      // Non-JSON response. Don't surface the raw body as the error
      // message — that's how Rails dev exception pages (HTML) end up
      // rendered into the screen via setErrorMessage. Drain the stream
      // (so the connection releases) but discard the contents. The
      // caller's toApiError will fall back to a clean
      // "Request failed (<status>)" string.
      await response.text().catch(() => '');
      return null;
    }
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  private toApiError(response: Response, parsed: unknown, options?: RequestOptions): ApiError {
    const errorBody = this.extractErrorBody(parsed);
    const message = errorBody?.message ?? `Request failed (${response.status})`;
    const code = errorBody?.code ?? this.codeFromStatus(response.status);

    if (response.status === 401) {
      if (!options?.suppressUnauthenticated) this.notifyUnauthenticated();
      return new UnauthenticatedError(message);
    }

    if (response.status === 429) {
      const retryAfter = this.parseRetryAfter(response);
      return new RateLimitError({ message, retryAfterSeconds: retryAfter });
    }

    if (response.status === 422 || response.status === 410 || response.status === 423) {
      return new ValidationError({
        code,
        message,
        details: errorBody?.details,
      });
    }

    if (response.status >= 500) {
      return new ServerError({ message, status: response.status });
    }

    return new ApiError({
      code,
      message,
      status: response.status,
      details: errorBody?.details,
    });
  }

  private toFetchError(err: unknown): ApiError {
    // `DOMException` isn't always defined under JSC / older RN runtimes —
    // guard the global before instanceof'ing so a missing polyfill doesn't
    // throw `ReferenceError: Property 'DOMException' doesn't exist`.
    const hasDOMException = typeof DOMException !== 'undefined';
    if (hasDOMException && err instanceof DOMException && err.name === 'AbortError') {
      return new TimeoutError();
    }
    // Only widen to Error.name='AbortError' when DOMException is unavailable —
    // avoids classifying a non-abort Error that happens to be named "AbortError"
    // as a timeout in environments that DO have the proper DOMException.
    if (!hasDOMException && err instanceof Error && err.name === 'AbortError') {
      return new TimeoutError();
    }
    if (err instanceof Error && err.message?.toLowerCase().includes('network')) {
      return new NetworkError(err.message);
    }
    return new NetworkError();
  }

  private extractErrorBody(parsed: unknown): ApiErrorBody | null {
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    // Standard envelope: { error: { code, message, details? } }
    if (obj.error && typeof obj.error === 'object') {
      return obj.error as ApiErrorBody;
    }
    // Rails-style bare string: { error: "step_out_of_order", active_step_key: "x" }.
    // Treat sibling fields as details so callers don't lose context.
    if (typeof obj.error === 'string') {
      const { error: _error, ...rest } = obj;
      return {
        code: obj.error,
        details: Object.keys(rest).length > 0 ? rest : undefined,
      };
    }
    if (typeof obj.message === 'string' || typeof obj.code === 'string') {
      return obj as unknown as ApiErrorBody;
    }
    return null;
  }

  private codeFromStatus(status: number): string {
    if (status === 401) return 'unauthenticated';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'not_found';
    if (status === 422) return 'unprocessable';
    if (status >= 500) return 'server_error';
    return 'unknown';
  }

  private parseRetryAfter(response: Response): number {
    const raw = response.headers.get(HEADERS.retryAfter);
    if (!raw) return 60;
    const seconds = parseInt(raw, 10);
    if (!Number.isNaN(seconds)) return seconds;
    const dateMs = Date.parse(raw);
    if (!Number.isNaN(dateMs)) {
      return Math.max(0, Math.ceil((dateMs - Date.now()) / 1000));
    }
    return 60;
  }

  private notifyUnauthenticated(): void {
    this.unauthenticatedListeners.forEach((listener) => {
      try {
        listener();
      } catch (err) {
        if (__DEV__) console.warn('[api] unauthenticated listener threw', err);
      }
    });
  }
}

export const apiClient = new ApiClient();
